import { useCallback } from 'react';

export const useWinScales = (betBody, setBetBody) => {
  // Обновление конкретного Win Scale по индексу
  const updateWinScale = useCallback(
    (index, value) => {
      setBetBody((prev) => {
        const newWinsScale = [...prev.winsScale];
        newWinsScale[index] = value;
        return { ...prev, winsScale: newWinsScale };
      });
    },
    [setBetBody],
  );

  // Удаление Win Scale по индексу
  const removeWinScale = useCallback(
    (index) => {
      setBetBody((prev) => ({
        ...prev,
        winsScale: prev.winsScale.filter((_, i) => i !== index),
      }));
    },
    [setBetBody],
  );

  // Добавление нового пустого Win Scale
  const addWinScale = useCallback(() => {
    setBetBody((prev) => ({
      ...prev,
      winsScale: [...prev.winsScale, ''],
    }));
  }, [setBetBody]);

  // Добавление предустановленного Win Scale
  const addPresetWinScale = useCallback(
    (preset) => {
      setBetBody((prev) => ({
        ...prev,
        winsScale: [...prev.winsScale, preset],
      }));
    },
    [setBetBody],
  );

  // Дублирование Win Scale
  const duplicateWinScale = useCallback(
    (index) => {
      setBetBody((prev) => {
        const scaleToDuplicate = prev.winsScale[index];
        return {
          ...prev,
          winsScale: [...prev.winsScale, scaleToDuplicate],
        };
      });
    },
    [setBetBody],
  );

  // Перемещение Win Scale вверх
  const moveWinScaleUp = useCallback(
    (index) => {
      if (index <= 0) return;

      setBetBody((prev) => {
        const newWinsScale = [...prev.winsScale];
        [newWinsScale[index - 1], newWinsScale[index]] = [newWinsScale[index], newWinsScale[index - 1]];
        return { ...prev, winsScale: newWinsScale };
      });
    },
    [setBetBody],
  );

  // Перемещение Win Scale вниз
  const moveWinScaleDown = useCallback(
    (index) => {
      setBetBody((prev) => {
        if (index >= prev.winsScale.length - 1) return prev;

        const newWinsScale = [...prev.winsScale];
        [newWinsScale[index], newWinsScale[index + 1]] = [newWinsScale[index + 1], newWinsScale[index]];
        return { ...prev, winsScale: newWinsScale };
      });
    },
    [setBetBody],
  );

  // Очистка всех Win Scales
  const clearAllWinScales = useCallback(() => {
    if (window.confirm('Очистити всі Win Scales?')) {
      setBetBody((prev) => ({
        ...prev,
        winsScale: [],
      }));
    }
  }, [setBetBody]);

  // Установка предустановленных наборов Win Scales
  const setPresetWinScales = useCallback(
    (presetType) => {
      let presetScales = [];

      switch (presetType) {
        case 'basic':
          presetScales = ['<100', '100-500', '>500'];
          break;
        case 'detailed':
          presetScales = ['<50', '50-100', '100-250', '250-500', '500-1000', '>1000'];
          break;
        case 'simple':
          presetScales = ['<500', '>500'];
          break;
        case 'advanced':
          presetScales = ['<25', '25-50', '50-100', '100-200', '200-500', '500-1000', '1000-2500', '>2500'];
          break;
        default:
          return;
      }

      setBetBody((prev) => ({
        ...prev,
        winsScale: presetScales,
      }));
    },
    [setBetBody],
  );

  // Валидация Win Scales
  const validateWinScales = useCallback(() => {
    const errors = [];
    const winsScale = betBody.winsScale;

    winsScale.forEach((scale, index) => {
      if (!scale || scale.trim() === '') {
        errors.push(`Win Scale ${index + 1} не может быть пустым`);
      } else {
        // Проверяем формат (примеры: "100-500", ">1000", "<100")
        const validFormats = [
          /^\d+-\d+$/, // 100-500
          /^>\d+$/, // >1000
          /^<\d+$/, // <100
          /^=\d+$/, // =100
          /^\d+$/, // 100
        ];

        const isValid = validFormats.some((regex) => regex.test(scale.trim()));
        if (!isValid) {
          errors.push(`Win Scale ${index + 1} має невірний формат: "${scale}"`);
        }
      }
    });

    return errors;
  }, [betBody.winsScale]);

  // Автоматическое создание логической последовательности Win Scales
  const generateLogicalSequence = useCallback(
    (minValue, maxValue, steps) => {
      if (steps < 2 || minValue >= maxValue) return;

      const scales = [];
      const stepSize = (maxValue - minValue) / (steps - 1);

      for (let i = 0; i < steps; i++) {
        const from = Math.round(minValue + i * stepSize);
        const to = i === steps - 1 ? maxValue : Math.round(minValue + (i + 1) * stepSize) - 1;

        if (i === 0) {
          scales.push(`<${to + 1}`);
        } else if (i === steps - 1) {
          scales.push(`>${from - 1}`);
        } else {
          scales.push(`${from}-${to}`);
        }
      }

      setBetBody((prev) => ({
        ...prev,
        winsScale: scales,
      }));
    },
    [setBetBody],
  );

  return {
    // Core actions
    updateWinScale,
    removeWinScale,
    addWinScale,

    // Advanced actions
    addPresetWinScale,
    duplicateWinScale,
    moveWinScaleUp,
    moveWinScaleDown,
    clearAllWinScales,
    setPresetWinScales,
    generateLogicalSequence,

    // Utilities
    validateWinScales,
  };
};
